import {
  ApiGeneralDataResponse,
  BacktestingRunType,
  DisplayCurrency,
  ExchangeName,
} from "@shared/types";

export interface SubGroupReview {
  approved_viewed?: boolean;
  creation_timestamp: number;
  current_user_comment?: boolean;
  full_name: string;
  message: string;
  stars: number;
  status?: SubGroupReviewStatus;
  user_id: number;
  rejection_reason?: SubGroupReviewRejectionReason;
}

export enum SubGroupReviewStatus {
  PENDING = "Pending",
  APPROVED = "Approved",
  REJECTED = "Rejected",
  DEACTIVATED = "Deactivated",
}

export enum SubGroupReviewRejectionReason {
  OBSCENE_LANGUAGE = 1,
  MISLEADING_OR_INACCURATE_INFORMATION = 2,
  FOCUS_ON_CHANNEL = 3,
  CONFLICT_OF_INTEREST = 4,
  OTHER = 5,
}

export interface SubGroupReviewsData {
  allow_review: boolean;
  avg_stars: number | null;
  reviews: SubGroupReview[];
  days_until_review: number;
  disallowed_review_reason?: string;
}

export const BACKTESTING_TIMEFRAMES = [7, 30, 90, 180];
export type BacktestingTimeframe = (typeof BACKTESTING_TIMEFRAMES)[number];

export type ScoreBreakdownType = {
  [key in BacktestingTimeframe]: ScoreValue;
};

export type ScoreValue = {
  value: number;
  weighted_score: number;
  weight: number;
};

export type BacktestingScoreBreakdown = {
  winning_losing: ScoreBreakdownType;
  beats_the_market: ScoreBreakdownType;
  low_leverage: ScoreBreakdownType;
  max_drawdown: ScoreBreakdownType;
  avg_trade_time: ScoreBreakdownType;
  has_stop_loss: ScoreBreakdownType;
  avg_rating: ScoreBreakdownType;
  is_free_or_has_free_trial: ScoreBreakdownType;
  age: ScoreBreakdownType;
  pnl: ScoreBreakdownType;
  number_of_trades: ScoreBreakdownType;
  sharpe_ratio: ScoreBreakdownType;
  sortino_ratio: ScoreBreakdownType;
  pnl_standard_deviation: ScoreBreakdownType;
};

export interface MarketplaceSubGroup {
  group_id: number;
  name: string;
  image_path: string;
  price_number: number;
  avg_stars: number | null;
  is_new: boolean;
  has_free_trial: boolean;
  is_best_seller: boolean;
  discount: number;
  num_of_reviewers: number;
  supported_exchanges: ExchangeName[];
  number_of_subscribers: number;
  sorting_score: number;
  backtesting_score: number;
  backtesting_score_breakdown: BacktestingScoreBreakdown;
  timeframe_to_backtesting_run_data?: {
    [key in BacktestingTimeframe]: BacktestingRunType;
  };
  average_weekly_signals: number;
  last_month_num_of_signals: number;
  subscription_price_usd: number;
  is_free: boolean;
  join_group_link: string | null;
}

export type MarketplaceResponse = ApiGeneralDataResponse<any[][]>;
export type SubGroupOverviewResponse = ApiGeneralDataResponse<SubGroupOverview>;

export enum GroupSubscriptionStatus {
  NO_SUBSCRIPTION = 1,
  IN_FREE_TRIAL = 2,
  FREE_TRIAL_ENDED = 3,
  ACTIVE_FINITE = 4,
  EXPIRED = 5,
  ACTIVE_RENEWAL = 6,
  HAS_UNTRIED_FREE_TRIAL = 7,
  NO_PLANS = 8,
}

interface GroupSubscriptionPlan {
  currency: number;
  discount: number;
  free_trial: boolean;
  group_subscription_plan_id: number;
  period: number;
  price: number;
}

export type SubGroupOverview = Pick<
  MarketplaceSubGroup,
  | "group_id"
  | "name"
  | "image_path"
  | "price_number"
  | "avg_stars"
  | "is_new"
  | "has_free_trial"
  | "is_best_seller"
  | "discount"
  | "num_of_reviewers"
  | "supported_exchanges"
  | "number_of_subscribers"
  | "sorting_score"
  | "subscription_price_usd"
  | "average_weekly_signals"
  | "is_free"
  | "backtesting_score"
  | "backtesting_score_breakdown"
  | "timeframe_to_backtesting_run_data"
  | "last_month_num_of_signals"
> & {
  contact_info: SubGroupContactInfo;
  creation_date: number;
  group_description: string | null;
  join_group_link: string | null;
  number_of_subscribers: number;
  price: number | null;
  price_currency: DisplayCurrency | null;
  supported_exchanges_info: SubGroupSupportedExchanges;
  top_signals: TopSignal[];
  group_subscription_status: GroupSubscriptionStatus;
  button_text: string;
  group_subscriptions?: GroupSubscriptionPlan[];
};
export type SubGroupOverviewRequest = Pick<MarketplaceSubGroup, "group_id">;

export interface MarketplaceExchangeInfo {
  number_of_signals: number;
}

export interface SubGroupSupportedExchanges {
  [exchangeName: string]: MarketplaceExchangeInfo;
}

export interface TopSignal {
  symbol: string;
  exchange: string;
  change: number;
}

export interface SocialMediaInfo {
  image_link: string;
  link: string;
}

export interface SubGroupContactInfo {
  [socialMedia: string]: SocialMediaInfo;
}

export type SubGroupReviewsRequest = Pick<MarketplaceSubGroup, "group_id">;
export type SubGroupReviewsResponse =
  ApiGeneralDataResponse<SubGroupReviewsData>;
export type SubGroupSetReviewRequest = Pick<MarketplaceSubGroup, "group_id"> &
  Pick<SubGroupReview, "stars" | "message"> & {
    is_edit: boolean;
  };

export interface ReviewInput {
  reviewStars: number;
  reviewInput: string;
}

export interface MarketplacePaymentDetails {
  price: number;
  title: string;
  payment_url: string;
  relevant_date: number;
  status: string;
  payment_option: string;
  image_path: string;
  period: string;
  coin: "USD";
}
